/**
 * StakeholderRegisterPage - ISO 22301 Clause 4.1-4.2
 * Manage interested parties and their requirements for BCMS
 * BCM Manager only - includes auto-discovery from PDCA modules
 */
import { useState, useEffect, useCallback } from "react";
import StepDialog from "../components/StepDialog";
import ReadinessBadge from "../components/ReadinessBadge";import { useAuth } from "../context/AuthContext";
import { resolveRoleLabel } from "../lib/roleLabel";
import AISuggestionsBanner from "../components/AISuggestionsBanner";
import { persistAcceptedSuggestions, suggestionName } from "../utils/acceptSuggestions";
import AIPowerSearch from "../components/AIPowerSearch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Edit,
  Trash2,
  Building2,
  User,
  Briefcase,
  Shield,
  FileText,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  RefreshCw,
  Search,
  Download,
  Scan,
  Import,
  Lock,
  Eye,
  ChevronRight,
  Target,
  ClipboardCheck,
  Activity,
  GraduationCap,
  Settings,
  Columns,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  X,
  Check,
  Sparkles,
} from "lucide-react";

const STAKEHOLDER_TYPES = [
  { id: "internal", name: "Internal", icon: Building2, color: "bg-blue-100 text-blue-700" },
  { id: "external", name: "External", icon: User, color: "bg-emerald-100 text-emerald-700" },
  { id: "regulatory", name: "Regulatory", icon: Shield, color: "bg-purple-100 text-purple-700" },
  { id: "supplier", name: "Supplier/Vendor", icon: Briefcase, color: "bg-amber-100 text-amber-700" },
  { id: "auditor", name: "Auditor", icon: Eye, color: "bg-rose-100 text-rose-700" },
];

// Fixed columns rendered by the Manual Stakeholders table (see table header below).
const MANUAL_TABLE_COLUMNS = ["Stakeholder", "Type", "Requirements", "Influence", "Interest", "Next Review", "Actions"];

const PDCA_MODULE_ICONS = {
  "PLAN": Target,
  "DO": Activity,
  "CHECK": ClipboardCheck,
  "ACT": RefreshCw,
};

const INFLUENCE_LEVELS = [
  { id: "high", name: "High", color: "bg-red-100 text-red-700" },
  { id: "medium", name: "Medium", color: "bg-amber-100 text-amber-700" },
  { id: "low", name: "Low", color: "bg-emerald-100 text-emerald-700" },
];

const INTEREST_LEVELS = [
  { id: "high", name: "High Interest", color: "bg-red-100 text-red-700" },
  { id: "medium", name: "Medium Interest", color: "bg-amber-100 text-amber-700" },
  { id: "low", name: "Low Interest", color: "bg-emerald-100 text-emerald-700" },
];

export default function StakeholderRegisterPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stakeholders, setStakeholders] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [stakeholderStep, setStakeholderStep] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [aiSearchResults, setAiSearchResults] = useState(null);
  
  // Auto-discovery state
  const [showDiscoverDialog, setShowDiscoverDialog] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoveredStakeholders, setDiscoveredStakeholders] = useState([]);
  const [selectedDiscovered, setSelectedDiscovered] = useState([]);
  const [importing, setImporting] = useState(false);
  const [discoverActiveTab, setDiscoverActiveTab] = useState("all");

  const [formData, setFormData] = useState({
    name: "",
    type: "internal",
    description: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    requirements: "",
    expectations: "",
    influence_level: "medium",
    interest_level: "medium",
    engagement_strategy: "",
    communication_frequency: "quarterly",
    last_review_date: "",
    next_review_date: "",
    notes: "",
    custom_fields: {},
  });

  // Column configuration - all available database attributes
  const ALL_STAKEHOLDER_ATTRIBUTES = [
    { key: "name", name: "Name", type: "text", default: true },
    { key: "type", name: "Type", type: "dropdown", options: ["internal", "external", "regulatory", "supplier"], default: true },
    { key: "role", name: "Role", type: "text", default: true },
    { key: "contact_email", name: "Email", type: "email", default: true },
    { key: "contact_phone", name: "Phone", type: "phone", default: true },
    { key: "source", name: "Source", type: "text", default: true },
    { key: "description", name: "Description", type: "text", default: false },
    { key: "contact_name", name: "Contact Name", type: "text", default: false },
    { key: "requirements", name: "Requirements", type: "text", default: false },
    { key: "expectations", name: "Expectations", type: "text", default: false },
    { key: "influence_level", name: "Influence Level", type: "dropdown", options: ["high", "medium", "low"], default: false },
    { key: "interest_level", name: "Interest Level", type: "dropdown", options: ["high", "medium", "low"], default: false },
    { key: "engagement_strategy", name: "Engagement Strategy", type: "text", default: false },
    { key: "communication_frequency", name: "Communication Frequency", type: "dropdown", options: ["monthly", "quarterly", "annually", "as_needed"], default: false },
    { key: "last_review_date", name: "Last Review Date", type: "date", default: false },
    { key: "next_review_date", name: "Next Review Date", type: "date", default: false },
    { key: "notes", name: "Notes", type: "text", default: false },
  ];

  // Visible columns state (which attributes to show)
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('stakeholder_visible_columns');
    if (saved) return JSON.parse(saved);
    return ALL_STAKEHOLDER_ATTRIBUTES.filter(a => a.default).map(a => a.key);
  });
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  
  // Column filters state
  const [columnFilters, setColumnFilters] = useState({});
  
  const [showColumnDialog, setShowColumnDialog] = useState(false);

  // BCM Manager or Admin only
  const canAccess = user?.role === "bcm_manager" || user?.role === "admin";
  const canEdit = canAccess;

  // State for aggregated view (Task 8)
  const [aggregatedStakeholders, setAggregatedStakeholders] = useState([]);
  const [viewMode, setViewMode] = useState("aggregated"); // "aggregated" or "manual"

  // Persist accepted AI stakeholder suggestions via the SAME endpoint the manual
  // "Add Stakeholder" dialog uses (POST /stakeholders/). Provenance is stored in the
  // custom_fields metadata (mirrors the assets approach) — NOT in customer-visible
  // prose; description carries the suggestion's real descriptive detail.
  const handleAcceptStakeholderSuggestions = async (items) => {
    const result = await persistAcceptedSuggestions({
      items,
      existingKeys: stakeholders.map((s) => s.name),
      keyOf: (raw) => suggestionName(raw),
      createOne: async (raw) => {
        const o = raw && typeof raw === "object" ? raw : {};
        const type = ["internal", "external", "regulatory", "supplier"].includes(o.type) ? o.type : "external";
        const desc = o.description || o.role || o.regulatory_body || o.category || o.detail || "";
        const lvl = (v) => {
          const s = String(v || "").toLowerCase();
          return ["high", "medium", "low"].includes(s) ? s : undefined;
        };
        // Bare-string suggestions leave o={} → payload keeps only the fields
        // written today (name, type, description, custom_fields.source).
        const payload = {
          name: suggestionName(raw),
          type,
          description: String(desc),
          custom_fields: { source: "ai_suggestion" },
        };
        if (o.role) payload.role = String(o.role);
        if (o.requirements) payload.requirements = String(o.requirements);
        if (o.expectations) payload.expectations = String(o.expectations);
        if (lvl(o.influence_level)) payload.influence_level = lvl(o.influence_level);
        if (lvl(o.interest_level)) payload.interest_level = lvl(o.interest_level);
        if (o.engagement_strategy) payload.engagement_strategy = String(o.engagement_strategy);
        if (o.communication_frequency) payload.communication_frequency = String(o.communication_frequency);
        await api.post("/stakeholders/", payload);
      },
    });
    await fetchStakeholders();
    return result;
  };


  const fetchStakeholders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/stakeholders/");
      setStakeholders(response.data || []);
    } catch (error) {
      console.error("Failed to fetch stakeholders:", error);
      toast.error("Failed to load stakeholders");
      setStakeholders([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  // Task 8: Fetch aggregated stakeholders from all sources
  const fetchAggregatedStakeholders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/stakeholders/aggregated");
      setAggregatedStakeholders(response.data || []);
    } catch (error) {
      console.error("Failed to fetch aggregated stakeholders:", error);
      // Fallback to regular stakeholders
      fetchStakeholders();
    } finally {
      setLoading(false);
    }
  }, [api, fetchStakeholders]);

  useEffect(() => {
    if (viewMode === "aggregated") {
      fetchAggregatedStakeholders();
    } else {
      fetchStakeholders();
    }
  }, [viewMode, fetchAggregatedStakeholders, fetchStakeholders]);

  // Save visible columns to localStorage
  useEffect(() => {
    localStorage.setItem('stakeholder_visible_columns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  // Toggle column visibility
  const toggleColumn = (key) => {
    setVisibleColumns(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Filter handler
  const handleColumnFilter = (key, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Clear all filters
  const clearAllFilters = () => {
    setColumnFilters({});
    setSearchTerm("");
  };

  // Get sorted and filtered data
  const getSortedFilteredData = (data) => {
    let result = [...data];
    
    // Apply column filters
    Object.keys(columnFilters).forEach(key => {
      const filterValue = columnFilters[key];
      if (filterValue && filterValue.trim()) {
        result = result.filter(item => {
          const value = item[key] || item.custom_fields?.[key] || '';
          return String(value).toLowerCase().includes(filterValue.toLowerCase());
        });
      }
    });
    
    // Apply search term
    if (searchTerm) {
      result = result.filter(s => 
        s.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.contact_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply type filter
    if (filterType !== "all") {
      result = result.filter(s => s.type === filterType);
    }
    
    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.key] || '';
        const bVal = b[sortConfig.key] || '';
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  };

  // Auto-discover stakeholders from PDCA modules
  const discoverStakeholders = async () => {
    setDiscovering(true);
    try {
      const response = await api.get("/stakeholders/discover/all");
      setDiscoveredStakeholders(response.data.discovered_stakeholders || []);
      setSelectedDiscovered([]);
      setShowDiscoverDialog(true);
      if (response.data.total_discovered === 0) {
        toast.info("No new stakeholders found in PDCA modules");
      } else {
        toast.success(`Found ${response.data.total_discovered} stakeholders across PDCA modules`);
      }
    } catch (error) {
      console.error("Failed to discover stakeholders:", error);
      toast.error("Failed to scan PDCA modules");
    } finally {
      setDiscovering(false);
    }
  };

  // Import selected discovered stakeholders
  const importSelectedStakeholders = async () => {
    if (selectedDiscovered.length === 0) {
      toast.error("Please select stakeholders to import");
      return;
    }

    setImporting(true);
    try {
      const toImport = discoveredStakeholders.filter((_, idx) => selectedDiscovered.includes(idx));
      const response = await api.post("/stakeholders/import-discovered", {
        stakeholders: toImport
      });
      
      toast.success(response.data.message);
      setShowDiscoverDialog(false);
      setSelectedDiscovered([]);
      fetchStakeholders();
    } catch (error) {
      console.error("Failed to import stakeholders:", error);
      toast.error("Failed to import stakeholders");
    } finally {
      setImporting(false);
    }
  };

  // Toggle selection of discovered stakeholder
  const toggleDiscoveredSelection = (idx) => {
    setSelectedDiscovered(prev => 
      prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]
    );
  };

  // Select all discovered stakeholders
  const selectAllDiscovered = () => {
    const filtered = getFilteredDiscovered();
    const indices = filtered.map((_, idx) => {
      // Find original index
      return discoveredStakeholders.findIndex(s => s === filtered[idx]);
    });
    setSelectedDiscovered(indices);
  };

  // Get filtered discovered stakeholders based on active tab
  const getFilteredDiscovered = () => {
    if (discoverActiveTab === "all") return discoveredStakeholders;
    return discoveredStakeholders.filter(s => s.source_module.startsWith(discoverActiveTab));
  };

  // Group discovered by PDCA phase
  const getDiscoveredByPhase = () => {
    const grouped = { PLAN: [], DO: [], CHECK: [], ACT: [] };
    discoveredStakeholders.forEach((s, idx) => {
      const phase = s.source_module.split(" - ")[0];
      if (grouped[phase]) {
        grouped[phase].push({ ...s, originalIndex: idx });
      }
    });
    return grouped;
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.type) {
      toast.error("Please fill in required fields");
      return;
    }

    setSubmitting(true);
    try {
      if (editingId) {
        const response = await api.put(`/stakeholders/${editingId}`, formData);
        setStakeholders(prev => prev.map(s => s.id === editingId ? response.data : s));
        toast.success("Stakeholder updated successfully");
      } else {
        const response = await api.post("/stakeholders/", formData);
        setStakeholders(prev => [...prev, response.data]);
        toast.success("Stakeholder added successfully");
      }
      resetForm();
      setShowDialog(false);
    } catch (error) {
      toast.error("Failed to save stakeholder");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (stakeholder) => {
    setFormData(stakeholder);
    setEditingId(stakeholder.id);
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this stakeholder?")) return;
    try {
      await api.delete(`/stakeholders/${id}`);
      setStakeholders(prev => prev.filter(s => s.id !== id));
      toast.success("Stakeholder deleted");
    } catch (error) {
      toast.error("Failed to delete stakeholder");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      type: "internal",
      description: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      requirements: "",
      expectations: "",
      influence_level: "medium",
      interest_level: "medium",
      engagement_strategy: "",
      communication_frequency: "quarterly",
      last_review_date: "",
      next_review_date: "",
      notes: "",
    });
    setEditingId(null);
    setStakeholderStep(0);
  };

  const getTypeInfo = (typeId) => STAKEHOLDER_TYPES.find(t => t.id === typeId) || STAKEHOLDER_TYPES[0];
  const getInfluenceInfo = (level) => INFLUENCE_LEVELS.find(l => l.id === level) || INFLUENCE_LEVELS[1];
  const getInterestInfo = (level) => INTEREST_LEVELS.find(l => l.id === level) || INTEREST_LEVELS[1];

  const filteredStakeholders = stakeholders.filter(s => {
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || s.type === filterType;
    return matchesSearch && matchesType;
  });

  // Stats - use active data source based on viewMode
  const activeStakeholders = viewMode === "aggregated" ? aggregatedStakeholders : stakeholders;
  const stats = {
    total: activeStakeholders.length,
    internal: activeStakeholders.filter(s => s.type === "internal").length,
    external: activeStakeholders.filter(s => s.type === "external").length,
    auditor: activeStakeholders.filter(s => s.type === "auditor").length,
    highInfluence: activeStakeholders.filter(s => s.influence_level === "high").length,
    needsReview: activeStakeholders.filter(s => {
      if (!s.next_review_date) return false;
      return new Date(s.next_review_date) <= new Date();
    }).length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  // Access control - BCM Manager only
  if (!canAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="p-6 bg-red-50 rounded-full">
          <Lock className="w-16 h-16 text-red-400" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900">Access Restricted</h2>
        <p className="text-slate-600 text-center max-w-md">
          The Stakeholder Register contains sensitive information and is restricted to BCM Manageristrators only.
          Contact your BCM Manager if you need access to stakeholder information.
        </p>
        <Badge className="bg-amber-100 text-amber-700">
          ISO 22301 Clause 4.2 - Confidential
        </Badge>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stakeholder-register-page">
      <AISuggestionsBanner module="stakeholder_register" onAccept={handleAcceptStakeholderSuggestions} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-blue-100 text-blue-700">PLAN Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 4.1-4.2</Badge>
            <Badge className="bg-rose-100 text-rose-700">
              <Lock className="w-3 h-3 mr-1" />
              BCM Manager Only
            </Badge>
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Stakeholder Register</h1>
          <p className="text-slate-600 mt-1">
            Central repository of all stakeholders from Organization, Applications, and Plans
          </p>
          <div className="mt-2">
            <ReadinessBadge
              total={activeStakeholders.length}
              complete={activeStakeholders.filter(s => s.engagement_strategy).length}
              label="Stakeholders"
            />
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* View Mode Toggle (Task 8) */}
          <div className="flex rounded-lg border overflow-hidden">
            <button
              onClick={() => setViewMode("aggregated")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === "aggregated" 
                  ? "bg-sky-700 text-white" 
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="view-mode-aggregated"
            >
              All Sources
            </button>
            <button
              onClick={() => setViewMode("manual")}
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === "manual" 
                  ? "bg-sky-700 text-white" 
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
              data-testid="view-mode-manual"
            >
              Manual Only
            </button>
          </div>
          <Button
            variant="outline"
            onClick={discoverStakeholders}
            disabled={discovering}
            className="bg-gradient-to-r from-indigo-50 to-purple-50 border-sky-200 hover:border-sky-300"
            data-testid="discover-stakeholders-btn"
          >
            {discovering ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Scan className="w-4 h-4 mr-2" />
            )}
            Auto-Discover
          </Button>
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => setShowColumnDialog(true)}
              className="border-slate-300"
              data-testid="manage-columns-btn"
            >
              <Columns className="w-4 h-4 mr-2" />
              Manage Columns
            </Button>
          )}
          {canEdit && (
            <Button
              onClick={() => { resetForm(); setShowDialog(true); }}
              className="bg-sky-700 hover:bg-sky-800"
              data-testid="add-stakeholder-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Stakeholder
            </Button>
          )}
        </div>
      </div>

      {/* Aggregated Sources Info (Task 8) */}
      {viewMode === "aggregated" && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2 text-sm text-blue-800">
              <Users className="w-4 h-4" />
              <span className="font-medium">Central Repository:</span>
              <span>Aggregating stakeholders from Executives, Team Leads, Application Vendors, Registered Users, and Manual Entries</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-sky-100 rounded-lg">
                <Users className="w-5 h-5 text-sky-700" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Stakeholders</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Internal</p>
                <p className="text-2xl font-bold text-slate-900">{stats.internal}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <User className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">External</p>
                <p className="text-2xl font-bold text-slate-900">{stats.external}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stakeholder-tile-auditor">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-100 rounded-lg">
                <Eye className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Auditor</p>
                <p className="text-2xl font-bold text-slate-900" data-testid="stakeholder-auditor-count">{stats.auditor}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">High Influence</p>
                <p className="text-2xl font-bold text-slate-900">{stats.highInfluence}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Calendar className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Needs Review</p>
                <p className="text-2xl font-bold text-slate-900">{stats.needsReview}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Power Search + Filters */}
      <Card>
        <CardContent className="pt-6 space-y-3">
          <AIPowerSearch
            module="stakeholders"
            onResults={(results, interpretation) => setAiSearchResults(results)}
            onClear={() => setAiSearchResults(null)}
            placeholder='Try: "show all stakeholders from IT department" or "find external vendors"...'
          />
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search stakeholders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {STAKEHOLDER_TYPES.map(type => (
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {Object.keys(columnFilters).length > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-red-600">
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
            )}
            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stakeholders Table */}
      <Card>
        <CardHeader className="flex flex-row justify-between items-center">
          <div>
            <CardTitle>{viewMode === "aggregated" ? "All Stakeholders (Central Repository)" : "Manual Stakeholders"}</CardTitle>
            <CardDescription>
              {viewMode === "aggregated" 
                ? "Auto-aggregated from Organization, Applications, Plans, and Manual entries"
                : "Document interested parties, their requirements, and engagement strategies"
            }</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {(viewMode === "manual" ? MANUAL_TABLE_COLUMNS.length : ALL_STAKEHOLDER_ATTRIBUTES.filter(a => visibleColumns.includes(a.key)).length)} columns visible
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {/* Aggregated View */}
          {viewMode === "aggregated" ? (
            getSortedFilteredData(aiSearchResults || aggregatedStakeholders).length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No stakeholders found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {ALL_STAKEHOLDER_ATTRIBUTES.filter(attr => visibleColumns.includes(attr.key)).map(attr => (
                        <TableHead key={attr.key} className="min-w-[120px]">
                          <div className="space-y-1">
                            {/* Column header with sort */}
                            <button
                              onClick={() => handleSort(attr.key)}
                              className="flex items-center gap-1 font-medium hover:text-sky-700 transition-colors"
                            >
                              {attr.name}
                              {sortConfig.key === attr.key ? (
                                sortConfig.direction === 'asc' ? (
                                  <ArrowUp className="w-3 h-3" />
                                ) : (
                                  <ArrowDown className="w-3 h-3" />
                                )
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-slate-300" />
                              )}
                            </button>
                            {/* Column filter */}
                            <Input
                              placeholder="Filter..."
                              value={columnFilters[attr.key] || ''}
                              onChange={(e) => handleColumnFilter(attr.key, e.target.value)}
                              className="h-6 text-xs px-1"
                            />
                          </div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getSortedFilteredData(aiSearchResults || aggregatedStakeholders).map((stakeholder) => {
                      const typeInfo = getTypeInfo(stakeholder.type);
                      return (
                        <TableRow key={stakeholder.id}>
                          {ALL_STAKEHOLDER_ATTRIBUTES.filter(attr => visibleColumns.includes(attr.key)).map(attr => (
                            <TableCell key={attr.key}>
                              {attr.key === 'type' ? (
                                <Badge variant="outline" className={`${typeInfo.color} ${typeInfo.text}`}>
                                  {typeInfo.name}
                                </Badge>
                              ) : attr.key === 'source' ? (
                                stakeholder.source === 'onboarding_enrichment' ? (
                                  <Badge
                                    variant="outline"
                                    className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] gap-1 whitespace-nowrap"
                                    title="Auto-created from onboarding auto-discovery. Review and complete this stakeholder record."
                                    data-testid={`stakeholder-auto-created-${stakeholder.id}`}
                                  >
                                    <Sparkles className="w-3 h-3" /> Auto-created
                                  </Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-xs">
                                    {stakeholder.source || '-'}
                                  </Badge>
                                )
                              ) : attr.key === 'influence_level' ? (
                                stakeholder.influence_level ? (
                                  <Badge className={getInfluenceInfo(stakeholder.influence_level).color}>{getInfluenceInfo(stakeholder.influence_level).name}</Badge>
                                ) : (
                                  <span className="text-sm text-slate-600">-</span>
                                )
                              ) : attr.key === 'interest_level' ? (
                                stakeholder.interest_level ? (
                                  <Badge className={getInterestInfo(stakeholder.interest_level).color}>{getInterestInfo(stakeholder.interest_level).name}</Badge>
                                ) : (
                                  <span className="text-sm text-slate-600">-</span>
                                )
                              ) : attr.key === 'role' ? (
                                <span className="text-sm text-slate-600">
                                  {stakeholder.role ? resolveRoleLabel(stakeholder) : '-'}
                                </span>
                              ) : (
                                <span className="text-sm text-slate-600">
                                  {stakeholder[attr.key] || '-'}
                                </span>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            /* Manual View - Original Table */
            filteredStakeholders.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No stakeholders registered yet</p>
                {canEdit && (
                  <Button className="mt-4" onClick={() => { resetForm(); setShowDialog(true); }}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Stakeholder
                  </Button>
                )}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Stakeholder</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Requirements</TableHead>
                    <TableHead>Influence</TableHead>
                    <TableHead>Interest</TableHead>
                    <TableHead>Next Review</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStakeholders.map((stakeholder) => {
                    const typeInfo = getTypeInfo(stakeholder.type);
                    const influenceInfo = getInfluenceInfo(stakeholder.influence_level);
                    const interestInfo = getInterestInfo(stakeholder.interest_level);
                    const needsReview = stakeholder.next_review_date && new Date(stakeholder.next_review_date) <= new Date();

                  return (
                    <TableRow key={stakeholder.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900">{stakeholder.name}</p>
                          <p className="text-sm text-slate-500 truncate max-w-[200px]">
                            {stakeholder.description}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={typeInfo.color}>{typeInfo.name}</Badge>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm text-slate-600 truncate max-w-[200px]">
                          {stakeholder.requirements || "-"}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge className={influenceInfo.color}>{influenceInfo.name}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={interestInfo.color}>{interestInfo.name}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {needsReview && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                          <span className={needsReview ? "text-amber-600 font-medium" : ""}>
                            {stakeholder.next_review_date || "-"}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {canEdit && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => handleEdit(stakeholder)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(stakeholder.id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog — TurboTax Progressive Disclosure */}
      <StepDialog
        open={showDialog}
        onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}
        title={editingId ? "Edit Stakeholder" : "Add New Stakeholder"}
        currentStep={stakeholderStep}
        onStepChange={setStakeholderStep}
        onSubmit={handleSubmit}
        onCancel={() => setShowDialog(false)}
        submitLabel={submitting ? "Saving..." : (editingId ? "Update Stakeholder" : "Add Stakeholder")}
        submitDisabled={submitting || !formData.name || !formData.type}
        saving={submitting}
        canAdvance={
          stakeholderStep === 0 ? !!formData.name && !!formData.type :
          true
        }
        steps={[
          { id: "identity", title: "Identity", subtitle: "Who is this stakeholder? Provide their name, type, and contact.", isComplete: !!formData.name && !!formData.type },
          { id: "requirements", title: "Requirements", subtitle: "What does this stakeholder need from your BCMS? (ISO 4.2)", isComplete: !!formData.requirements || !!formData.expectations },
          { id: "assessment", title: "Assessment", subtitle: "Rate this stakeholder's influence and interest level.", isComplete: true },
          { id: "engagement", title: "Engagement", subtitle: "How will you engage with and communicate to this stakeholder?", isComplete: false },
        ]}
      >
        {/* Step 0: Identity */}
        {stakeholderStep === 0 && (
          <div className="space-y-4" data-testid="step-identity">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Stakeholder Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Executive Leadership Team"
                data-testid="stakeholder-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Type *</Label>
              <div className="space-y-2">
                {STAKEHOLDER_TYPES.map(type => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, type: type.id })}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      formData.type === type.id
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`type-${type.id}`}
                  >
                    <p className="font-medium text-sm">{type.name}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Contact Name</Label>
              <Input
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                placeholder="Primary contact person"
                data-testid="contact-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Role</Label>
              <Input
                value={formData.role || ""}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                placeholder="e.g., Regulator, Auditor, Supplier Manager"
                data-testid="stakeholder-role-input"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-semibold">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this stakeholder group"
                rows={2}
                data-testid="description-input"
              />
            </div>
          </div>
        )}

        {/* Step 1: Requirements */}
        {stakeholderStep === 1 && (
          <div className="space-y-4" data-testid="step-requirements">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Requirements (What they need from BCMS)</Label>
              <Textarea
                value={formData.requirements}
                onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                placeholder="e.g., Regular status updates, compliance evidence, audit reports"
                rows={3}
                data-testid="requirements-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Expectations (What they expect)</Label>
              <Textarea
                value={formData.expectations}
                onChange={(e) => setFormData({ ...formData, expectations: e.target.value })}
                placeholder="e.g., Minimal disruption, quick recovery, regulatory compliance"
                rows={3}
                data-testid="expectations-input"
              />
            </div>
          </div>
        )}

        {/* Step 2: Assessment */}
        {stakeholderStep === 2 && (
          <div className="space-y-4" data-testid="step-assessment">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Influence Level</Label>
              <div className="space-y-2">
                {INFLUENCE_LEVELS.map(level => (
                  <button
                    key={level.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, influence_level: level.id })}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      formData.influence_level === level.id
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`influence-${level.id}`}
                  >
                    <p className="font-medium text-sm">{level.name}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Interest Level</Label>
              <div className="space-y-2">
                {INTEREST_LEVELS.map(level => (
                  <button
                    key={level.id}
                    type="button"
                    onClick={() => setFormData({ ...formData, interest_level: level.id })}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      formData.interest_level === level.id
                        ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-200'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`interest-${level.id}`}
                  >
                    <p className="font-medium text-sm">{level.name}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Engagement */}
        {stakeholderStep === 3 && (
          <div className="space-y-4" data-testid="step-engagement">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Engagement Strategy</Label>
              <Textarea
                value={formData.engagement_strategy}
                onChange={(e) => setFormData({ ...formData, engagement_strategy: e.target.value })}
                placeholder="How will you engage with this stakeholder?"
                rows={2}
                data-testid="engagement-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Communication Frequency</Label>
              <Select value={formData.communication_frequency} onValueChange={(v) => setFormData({ ...formData, communication_frequency: v })}>
                <SelectTrigger data-testid="frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                  <SelectItem value="as_needed">As Needed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Next Review Date</Label>
              <Input
                type="date"
                value={formData.next_review_date}
                onChange={(e) => setFormData({ ...formData, next_review_date: e.target.value })}
                data-testid="review-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Additional Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional information..."
                rows={2}
                data-testid="notes-input"
              />
            </div>
          </div>
        )}
      </StepDialog>

      {/* Auto-Discover Dialog */}
      <Dialog open={showDiscoverDialog} onOpenChange={setShowDiscoverDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scan className="w-5 h-5 text-sky-700" />
              Auto-Discovered Stakeholders
            </DialogTitle>
            <DialogDescription>
              Stakeholders found across all PDCA modules. Select the ones to import into the Stakeholder Register.
            </DialogDescription>
          </DialogHeader>

          {discoveredStakeholders.length === 0 ? (
            <div className="py-12 text-center">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No new stakeholders discovered</p>
              <p className="text-sm text-slate-400 mt-1">
                Add stakeholder information in BIA, Risk Assessment, Recovery Plans, and other modules
              </p>
            </div>
          ) : (
            <>
              {/* Stats Bar */}
              <div className="flex items-center gap-4 py-3 px-4 bg-slate-50 rounded-lg mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Total Found:</span>
                  <Badge>{discoveredStakeholders.length}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">Selected:</span>
                  <Badge className="bg-sky-100 text-sky-700">{selectedDiscovered.length}</Badge>
                </div>
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAllDiscovered}>
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setSelectedDiscovered([])}>
                    Clear
                  </Button>
                </div>
              </div>

              {/* PDCA Phase Tabs */}
              <Tabs value={discoverActiveTab} onValueChange={setDiscoverActiveTab} className="flex-1 flex flex-col overflow-hidden">
                <TabsList className="w-full justify-start">
                  <TabsTrigger value="all" className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    All ({discoveredStakeholders.length})
                  </TabsTrigger>
                  {Object.entries(getDiscoveredByPhase()).map(([phase, items]) => {
                    const Icon = PDCA_MODULE_ICONS[phase] || Target;
                    return (
                      <TabsTrigger key={phase} value={phase} className="flex items-center gap-1">
                        <Icon className="w-4 h-4" />
                        {phase} ({items.length})
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                <TabsContent value={discoverActiveTab} className="flex-1 overflow-auto mt-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">
                          <Checkbox 
                            checked={selectedDiscovered.length === discoveredStakeholders.length && discoveredStakeholders.length > 0}
                            onCheckedChange={(checked) => {
                              if (checked) selectAllDiscovered();
                              else setSelectedDiscovered([]);
                            }}
                          />
                        </TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Source Module</TableHead>
                        <TableHead>Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getFilteredDiscovered().map((stakeholder, idx) => {
                        const originalIdx = discoveredStakeholders.findIndex(s => s === stakeholder);
                        const typeInfo = STAKEHOLDER_TYPES.find(t => t.id === stakeholder.stakeholder_type) || STAKEHOLDER_TYPES[0];
                        const isSelected = selectedDiscovered.includes(originalIdx);

                        return (
                          <TableRow 
                            key={originalIdx} 
                            className={`cursor-pointer transition-colors ${isSelected ? "bg-sky-50" : "hover:bg-slate-50"}`}
                            onClick={() => toggleDiscoveredSelection(originalIdx)}
                          >
                            <TableCell>
                              <Checkbox 
                                checked={isSelected}
                                onCheckedChange={() => toggleDiscoveredSelection(originalIdx)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-slate-900">{stakeholder.name}</div>
                              {stakeholder.email && (
                                <div className="text-xs text-slate-500">{stakeholder.email}</div>
                              )}
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-slate-600">{stakeholder.role ? resolveRoleLabel(stakeholder) : '-'}</span>
                            </TableCell>
                            <TableCell>
                              <Badge className={typeInfo.color}>{typeInfo.name}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="font-normal">
                                {stakeholder.source_module}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-slate-500 truncate max-w-[200px] block">
                                {stakeholder.source_detail}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TabsContent>
              </Tabs>

              {/* Import Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t mt-4">
                <Button variant="outline" onClick={() => setShowDiscoverDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={importSelectedStakeholders} 
                  disabled={importing || selectedDiscovered.length === 0}
                  className="bg-sky-700 hover:bg-sky-800"
                >
                  {importing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Import className="w-4 h-4 mr-2" />
                  )}
                  Import {selectedDiscovered.length} Stakeholder{selectedDiscovered.length !== 1 ? "s" : ""}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Column Selection Dialog */}
      <Dialog open={showColumnDialog} onOpenChange={setShowColumnDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Columns className="w-5 h-5 text-sky-700" />
              Manage Visible Columns
            </DialogTitle>
            <DialogDescription>
              Select which database attributes to display as columns. Click on a column to toggle visibility.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setVisibleColumns(ALL_STAKEHOLDER_ATTRIBUTES.map(a => a.key))}
              >
                <Check className="w-4 h-4 mr-1" />
                Show All
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setVisibleColumns(ALL_STAKEHOLDER_ATTRIBUTES.filter(a => a.default).map(a => a.key))}
              >
                Reset to Default
              </Button>
            </div>

            {/* Column Selection Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {ALL_STAKEHOLDER_ATTRIBUTES.map(attr => {
                const isVisible = visibleColumns.includes(attr.key);
                return (
                  <div
                    key={attr.key}
                    onClick={() => toggleColumn(attr.key)}
                    className={`p-3 rounded-lg border cursor-pointer transition-all ${
                      isVisible 
                        ? 'bg-sky-50 border-sky-300 text-sky-700' 
                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{attr.name}</p>
                        <p className="text-xs text-slate-400">{attr.type}</p>
                      </div>
                      {isVisible ? (
                        <Check className="w-4 h-4 text-sky-700" />
                      ) : (
                        <div className="w-4 h-4 border rounded" />
                      )}
                    </div>
                    {attr.default && (
                      <Badge variant="outline" className="text-[10px] mt-1">Default</Badge>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="pt-4 border-t text-sm text-slate-500">
              <p><strong>Tip:</strong> Use column filters (in the table header) to filter by specific values. Click column headers to sort.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
